import { toLongDate } from '@utils/Dated';
import {
  getAssigneeOrganizationLabel,
  getCompanyDisplayLabel,
  getIssueUserDisplayLabel,
  getLocalizedLookupLabel,
  getLookupCode,
  getReporterContextLabel,
  getReporterDisplayLabel,
} from '../../components/utils';
import { UNSUPPORTED_STATUS_CODES } from '../../components/constants';

export const DETAIL_FORM_ID = 'ISSUE_DETAIL_PAGE_FORM';

const RESOLUTION_FIELDS = ['resolutionCode', 'resolutionNote'];

const getConfigCodes = (issueConfig, key) =>
  Array.isArray(issueConfig?.[key]) ? issueConfig[key] : [];

export const buildAssignUserQueryParams = ({ companyId, searchValue }) => ({
  page: 1,
  limit: 10,
  sortBy: 'email:ASC',
  assignableOnly: true,
  ...(companyId && { companyId }),
  ...(searchValue?.trim() && { email: searchValue.trim() }),
});

export const buildAssignUserValueQueryParams = ({ companyId, userId }) => ({
  page: 1,
  limit: 1,
  assignableOnly: true,
  userId,
  ...(companyId && { companyId }),
});

export const buildIssueAssignmentPayload = ({
  assigneeUserId,
  assigneeRole,
  recordVersion,
}) => ({
  assigneeUserId,
  assigneeRole,
  recordVersion,
});

// Single source of truth for the inline assignee-role/user local UI state,
// derived from the persisted issue. Used both to seed that state when the
// issue (re)loads and to roll it back after a failed /assign call, so the
// UI never shows an unpersisted role or assignee.
export const getAssignStateFromIssue = ({ issue, isAdminUser }) => ({
  assignRole: isAdminUser
    ? issue?.assigneeRole || 'ADMIN'
    : 'SERVICE_ADMINISTRATOR',
  assignUserId: issue?.assigneeUserId || '',
});

export const shouldShowAdminEscalationNotice = ({
  issue,
  isServiceAdministrator,
}) =>
  Boolean(
    isServiceAdministrator &&
    issue?.escalated &&
    issue?.assigneeRole === 'ADMIN',
  );

export const areIssueTriageActionsDisabled = ({ saving, detailFormDirty }) =>
  Boolean(saving || detailFormDirty);

export const handleIssueMutationConflict = async ({
  result,
  refreshIssue,
  showError,
}) => {
  if (result?.statusCode !== 409) {
    return false;
  }

  await refreshIssue();
  showError(result?.errors);
  return true;
};

export const getReporterUserId = issue =>
  issue?.reporterUserId ||
  issue?.reporter_user_id ||
  issue?.reporter?.id ||
  issue?.reporter?.userId;

export const getEditableDetailFields = ({ issue }) =>
  Array.isArray(issue?.capabilities?.editableFields)
    ? issue.capabilities.editableFields
    : [];

export const getIssueActionButtonAvailability = ({
  saving,
  detailFormDirty = false,
  editableDetailFields = [],
  canReopenIssue = false,
  capabilities,
  currentStatusCode,
  issueConfig,
  canResolveStatus,
}) => {
  const canUpdateIssue =
    capabilities?.actions?.canUpdateIssue ?? editableDetailFields.length > 0;
  const canUpdateStatus = capabilities?.actions?.canUpdateStatus ?? false;
  const canUpdateDetailFields =
    canUpdateIssue && editableDetailFields.length > 0;
  const terminalStatusCodes = getConfigCodes(
    issueConfig,
    'terminalStatusCodes',
  );
  const isTerminalStatus = terminalStatusCodes.includes(currentStatusCode);
  const canUpdateStatusInDetailForm = canUpdateStatus && !isTerminalStatus;
  const canSaveDetailForm =
    canUpdateDetailFields || canUpdateStatusInDetailForm;
  const canOpenResolveDialog = canUpdateStatus && (canResolveStatus ?? true);
  const canReopenCurrentIssue = canReopenIssue;

  return {
    cancelDisabled: !!saving,
    saveIssueHidden: !canSaveDetailForm,
    saveIssueDisabled: !!saving || !canSaveDetailForm || !detailFormDirty,
    updateStatusHidden: !canOpenResolveDialog || isTerminalStatus,
    updateStatusDisabled: !!saving || !canOpenResolveDialog || isTerminalStatus,
    reopenIssueHidden: !canReopenCurrentIssue,
    reopenIssueDisabled: !!saving || !canReopenCurrentIssue,
  };
};

export const getAllowedStatusCodesForIssue = issue =>
  Array.isArray(issue?.capabilities?.allowedStatusCodes)
    ? issue.capabilities.allowedStatusCodes
    : [];

export const getDetailStatusCodesForIssue = (
  issue,
  { isServiceProviderUser = false, issueConfig } = {},
) => {
  const currentStatusCode = issue?.status?.code;
  const excludedStatusCodes = new Set([
    ...UNSUPPORTED_STATUS_CODES,
    ...getConfigCodes(issueConfig, 'detailStatusExcludedCodes'),
  ]);

  return getAllowedStatusCodesForIssue(issue).filter(code => {
    if (code === currentStatusCode) {
      return true;
    }
    if (excludedStatusCodes.has(code)) {
      return false;
    }
    // Service Providers cancel via the dedicated Cancel Request action once a
    // request reaches Ready for Review, not via this dropdown (SXCL-2475).
    if (
      isServiceProviderUser &&
      code === 'CANCELLED' &&
      currentStatusCode === 'READY_FOR_REVIEW'
    ) {
      return false;
    }
    return true;
  });
};

// A status the inline detail-form Status field lets the user pick may still
// require a resolution reason (RESOLVED/CLOSED). That reason can only be
// captured through the existing Resolve dialog, so selecting such a status
// inline must redirect there instead of silently submitting it bare.
export const getInlineStatusSelectionRedirect = ({
  selectedStatusCode,
  currentStatusCode,
  resolutionStatusCodes = [],
}) =>
  selectedStatusCode &&
  selectedStatusCode !== currentStatusCode &&
  resolutionStatusCodes.includes(selectedStatusCode)
    ? selectedStatusCode
    : null;

export const getResolutionStatusCodesForIssue = (issue, issueConfig) => {
  const resolutionStatusCodes = getConfigCodes(
    issueConfig,
    'resolutionStatusCodes',
  );

  return getAllowedStatusCodesForIssue(issue).filter(code =>
    resolutionStatusCodes.includes(code),
  );
};

export const getDefaultResolutionStatusCode = (issue, issueConfig) => {
  const currentStatusCode = issue?.status?.code;
  const resolutionStatusCodes = getResolutionStatusCodesForIssue(
    issue,
    issueConfig,
  );

  return (
    resolutionStatusCodes.find(code => code !== currentStatusCode) ||
    resolutionStatusCodes[0] ||
    ''
  );
};

export const getReopenStatusCode = (issue, issueConfig) => {
  const reopenStatusCode = issueConfig?.reopenStatusCode;
  return getAllowedStatusCodesForIssue(issue).includes(reopenStatusCode)
    ? reopenStatusCode
    : '';
};

export const canReopenIssue = ({ issue, issueConfig }) =>
  getConfigCodes(issueConfig, 'reopenableStatusCodes').includes(
    issue?.status?.code,
  ) && !!getReopenStatusCode(issue, issueConfig);

// Read-only rendering shows the lookup label, not the raw code
// (e.g. "Issue" instead of "BUG").
const READ_ONLY_LABEL_FIELD_NAMES = {
  typeCode: 'typeLabel',
  priorityCode: 'priorityLabel',
  componentCode: 'componentLabel',
  statusCode: 'statusLabel',
  resolutionCode: 'resolutionLabel',
};

const toReadOnlyField = field => {
  const { required, ...readOnlyField } = field;
  return {
    ...readOnlyField,
    name: READ_ONLY_LABEL_FIELD_NAMES[field.name] || field.name,
    control: 'IssueReadOnly',
    readOnly: true,
  };
};

// Resolution details only make sense once a request was resolved/closed.
const RESOLUTION_ONLY_FIELD_NAMES = new Set([
  'resolvedAtLabel',
  'resolutionNote',
  'resolutionCode',
  'resolutionLabel',
]);

// Assignment is SA/Admin-internal triage detail; Service Providers never see
// who a request is assigned to (SXCL-2475).
export const isDetailFieldVisibleForRole = (field, { isServiceProviderUser }) =>
  !(isServiceProviderUser && field.name === 'assignedToLabel');

// The Additional Info audit tabs are not available to service-provider users
// (the backing activity APIs reject them as well).
export const isDetailSectionVisibleForRole = (
  section,
  { isServiceProviderUser },
) => !(isServiceProviderUser && section.locale.id === 'ADDITIONAL_INFO');

export const getRoleAwareIssueDetailSections = ({
  sections,
  editableFields,
  canManageStatus,
  showResolutionFields = true,
}) =>
  sections.map(section => ({
    ...section,
    children: section.children
      .filter(
        field =>
          showResolutionFields || !RESOLUTION_ONLY_FIELD_NAMES.has(field.name),
      )
      .map(field => {
        if (
          field.control === 'IssueReadOnly' ||
          (field.name === 'statusCode' && canManageStatus) ||
          (!RESOLUTION_FIELDS.includes(field.name) &&
            field.name !== 'statusCode' &&
            editableFields.includes(field.name))
        ) {
          return field;
        }

        return toReadOnlyField(field);
      }),
  }));

const formatIssueDateTime = value => (value ? toLongDate(value) : '-');

export const getReporterLabel = (issue, formatMessage) => {
  const reporterUserId = getReporterUserId(issue);
  const reporterDisplayLabel = getReporterDisplayLabel(issue?.reporter);
  const reporterActivity = issue?.activity?.find?.(
    activity =>
      activity?.actorUserId === reporterUserId && activity?.actorDisplayName,
  );

  const displayLabel =
    (reporterDisplayLabel !== reporterUserId ? reporterDisplayLabel : '') ||
    reporterActivity?.actorDisplayName ||
    reporterUserId ||
    '';

  return getReporterContextLabel(
    {
      ...issue?.reporter,
      displayName: displayLabel,
    },
    formatMessage,
  );
};

export const getCompanyLabel = (issue, formatMessage) =>
  getCompanyDisplayLabel(issue?.company, formatMessage);

export const getAssignedUserLabel = (issue, formatMessage) => {
  const displayName = getIssueUserDisplayLabel(issue?.assignee);
  const email = issue?.assignee?.email || '';

  if (displayName || email) {
    return [displayName, email !== displayName ? email : '']
      .filter(Boolean)
      .join(' · ');
  }

  if (issue?.assigneeUserId) {
    return formatMessage
      ? formatMessage({
          id: 'ASSIGNED_USER_UNAVAILABLE',
          defaultMessage: 'Assigned user unavailable',
        })
      : 'Assigned user unavailable';
  }

  return formatMessage
    ? formatMessage({
        id: 'UNASSIGNED',
        defaultMessage: 'Unassigned',
      })
    : 'Unassigned';
};

export const getAssignedToLabel = (issue, formatMessage) => {
  const organizationLabel = getAssigneeOrganizationLabel(issue);
  const assignedUserLabel = getAssignedUserLabel(issue, formatMessage);
  const hasAssignedUser = Boolean(
    issue?.assigneeUserId || getIssueUserDisplayLabel(issue?.assignee),
  );

  if (!hasAssignedUser) {
    return [organizationLabel, assignedUserLabel].filter(Boolean).join(' · ');
  }

  return [assignedUserLabel, organizationLabel].filter(Boolean).join(' · ');
};

const getIssueResolutionLookup = issue =>
  issue?.resolution || issue?.resolutionCode || issue?.resolutionStatus;

export const getIssueHeaderFields = (issue, formatMessage) => ({
  issueNumber: issue?.issueNumber || '-',
  statusLabel: getLocalizedLookupLabel(issue?.status, 'STATUS', formatMessage),
  priorityLabel: getLocalizedLookupLabel(
    issue?.priority,
    'PRIORITY',
    formatMessage,
  ),
  resolutionLabel: getLocalizedLookupLabel(
    getIssueResolutionLookup(issue),
    'RESOLUTION_CODE',
    formatMessage,
  ),
});

export const normalizeIssueForDetailPage = (issue, formatMessage) => ({
  issueNumber: issue?.issueNumber || '-',
  reporterLabel: getReporterLabel(issue, formatMessage),
  companyLabel: getCompanyLabel(issue, formatMessage),
  assignedToLabel: getAssignedToLabel(issue, formatMessage),
  statusLabel: getLocalizedLookupLabel(issue?.status, 'STATUS', formatMessage),
  priorityLabel: getLocalizedLookupLabel(
    issue?.priority,
    'PRIORITY',
    formatMessage,
  ),
  typeLabel: getLocalizedLookupLabel(issue?.type, 'TYPE', formatMessage),
  componentLabel: getLocalizedLookupLabel(
    issue?.component,
    'COMPONENT',
    formatMessage,
  ),
  createdDateTimeLabel: formatIssueDateTime(issue?.createdDateTime),
  lastModifiedDateTimeLabel: formatIssueDateTime(issue?.lastModifiedDateTime),
  firstResponseAtLabel: formatIssueDateTime(issue?.firstResponseAt),
  resolvedAtLabel: formatIssueDateTime(issue?.resolvedAt),
  dueAtLabel: formatIssueDateTime(issue?.dueAt),
  title: issue?.title || '',
  description: issue?.description || '',
  typeCode: issue?.type?.code || '',
  priorityCode: issue?.priority?.code || '',
  componentCode: issue?.component?.code || '',
  statusCode: issue?.status?.code || '',
  resolutionCode: getLookupCode(getIssueResolutionLookup(issue)),
  resolutionLabel: getLocalizedLookupLabel(
    getIssueResolutionLookup(issue),
    'RESOLUTION_CODE',
    formatMessage,
  ),
  resolutionNote: issue?.resolutionNote || '',
});
